import { Blockquote } from '@ui';
import { useTranslation } from 'react-i18next';
import ServiceHoursRow from './ServiceHoursRow';
import { ServiceHoursPreviewRow, ServiceHoursRowDecision } from './types';

interface Props {
    rows: ServiceHoursPreviewRow[];
    decisions: Record<number, ServiceHoursRowDecision>;
    memberCategories: Record<string, number[]>;
    categoryId?: number;
    onChange: (bookingId: number, decision: ServiceHoursRowDecision) => void;
}

/**
 * Preview table, one row per selected booking. Deliberately has no duplicate-target
 * check: two bookings by the same person are two credits, not a collision.
 */
const ServiceHoursPreviewTable = ({ rows, decisions, memberCategories, categoryId, onChange }: Props) => {
    const { t } = useTranslation('bookings');

    // Nothing survived the backend's filtering (e.g. an explicitly picked cancelled
    // booking): explain why instead of rendering headers over an empty table.
    if (rows.length === 0) {
        return <Blockquote color="warning">{t('booking.creditServiceHours.emptyPreview')}</Blockquote>;
    }

    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse">
                <thead>
                    <tr className="tw-text-left tw-text-sm tw-text-accents-6">
                        <th className="tw-p-2">{t('booking.creditServiceHours.columns.booking')}</th>
                        <th className="tw-p-2">{t('booking.creditServiceHours.columns.member')}</th>
                        <th className="tw-p-2">{t('booking.creditServiceHours.columns.hours')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const decision = decisions[row.booking_id] ?? {
                            action: 'skip' as const,
                            member_id: null,
                            hours: '' as const
                        };

                        return (
                            <ServiceHoursRow
                                key={row.booking_id}
                                row={row}
                                decision={decision}
                                categoryId={categoryId}
                                memberCategoryIds={
                                    decision.member_id ? memberCategories[String(decision.member_id)] : undefined
                                }
                                onChange={(next) => onChange(row.booking_id, next)}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ServiceHoursPreviewTable;
